import React, { useState, useEffect } from 'react';
import {
  Search, Package, MapPin, Clock, CheckCircle,
  AlertCircle, FileText, Printer, Calendar, HelpCircle, X
} from 'lucide-react';
import apiClient from '../services/api';

const VIOLET      = '#667eea';
const VIOLET_DARK = '#764ba2';

/* ── Helpers ── */
function fmtDateTime(dateStr, timeStr) {
  if (!dateStr) return '';
  try {
    const d    = new Date(dateStr);
    const date = d.toLocaleDateString('en-IN', { day:'2-digit', month:'2-digit', year:'numeric' });
    const time = timeStr
      ? timeStr.slice(0, 5)
      : d.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12: false });
    return `${date} ${time}`;
  } catch { return dateStr; }
}

function fmtDate(str) {
  if (!str) return '-';
  try { return new Date(str).toLocaleDateString('en-IN', { day:'2-digit', month:'numeric', year:'numeric' }); }
  catch { return str; }
}

/* Bold first word + bold ALL-CAPS tokens */
function EventCell({ text }) {
  if (!text) return <span>—</span>;
  const sp = text.indexOf(' ');
  if (sp === -1) return <span className="font-extrabold text-violet-800">{text}</span>;
  const keyword = text.slice(0, sp);
  const rest    = text.slice(sp + 1);
  const parts   = rest.split(/(\b[A-Z][A-Z\-]{1,}\b)/g);
  return (
    <span>
      <span className="font-extrabold text-violet-800">{keyword}</span>{' '}
      {parts.map((p, i) =>
        /^[A-Z][A-Z\-]{1,}$/.test(p)
          ? <span key={i} className="font-bold text-gray-800">{p}</span>
          : <span key={i} className="text-gray-700">{p}</span>
      )}
    </span>
  );
}

/* Courier status badge — blue */
function CourierBadge({ name }) {
  if (!name) return <span>-</span>;
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[0.78rem] font-bold whitespace-nowrap bg-blue-100 text-blue-800">{name}</span>
  );
}

/* Delivery status badge */
function DeliveryBadge({ value }) {
  if (!value) return <span>-</span>;
  const isDelivered = value.toLowerCase() === 'delivered';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[0.78rem] font-bold whitespace-nowrap ${isDelivered ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
      {value}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Main Component
══════════════════════════════════════════════════════════════════════════════ */
const ConsignmentTracking = () => {
  const [searchQuery,    setSearchQuery]    = useState('');
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [trackingData,   setTrackingData]   = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showHelp,       setShowHelp]       = useState(false);

  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem('recent_tracking_searches') || '[]');
      setRecentSearches(s.slice(0, 5));
    } catch { /* ignore */ }
  }, []);

  const saveRecentSearch = (no) => {
    try {
      let list = JSON.parse(localStorage.getItem('recent_tracking_searches') || '[]');
      list = [no, ...list.filter(s => s !== no)].slice(0, 10);
      localStorage.setItem('recent_tracking_searches', JSON.stringify(list));
      setRecentSearches(list.slice(0, 5));
    } catch { /* ignore */ }
  };

  const doSearch = async (query) => {
    const q = (query || searchQuery).trim();
    if (!q) { setError('Please enter a consignment number'); return; }
    setLoading(true); setError(''); setTrackingData(null);
    try {
      const res = await apiClient.get('/ConsignmentTracking.php', { params: { consignmentNo: q } });
      if (res.data.success) { setTrackingData(res.data.data); saveRecentSearch(q); }
      else setError(res.data.message || 'Consignment not found');
    } catch (err) { setError('Error: ' + (err.response?.data?.message || err.message)); }
    finally { setLoading(false); }
  };

  const handleSearch      = (e) => { e?.preventDefault(); doSearch(); };
  const handleQuickSearch = (no) => { setSearchQuery(no); doSearch(no); };

  const c             = trackingData?.consignment;
  const timeline      = trackingData?.timeline      || [];
  const statusHistory = trackingData?.statusHistory || [];

  const trackingRows = timeline.map((evt, idx) => ({
    id:       idx,
    dateTime: fmtDateTime(evt.EventDate, evt.EventTime),
    txn:      c?.ConsignmentNo || '',
    location: evt.Location || '',
    event:    evt.EventName
              + (evt.Description && evt.Description !== evt.EventName ? ', ' + evt.Description : '')
              + (evt.PersonName ? ', ' + evt.PersonName : ''),
  }));

  const dueDate = c?.EstimatedDeliveryDate
    ? new Date(c.EstimatedDeliveryDate).toLocaleDateString('en-IN', { day:'2-digit', month:'2-digit', year:'numeric' })
    : null;

  return (
    <div className="p-5 max-w-[1200px] mx-auto box-border">
      <style>{`
        @media print { .no-print { display:none !important; } }
        .tr-hover:hover { background:#f5f3ff !important; }
        .sh-hover:hover { background:#f8fafc !important; }
        .chip:hover { background:linear-gradient(135deg,#667eea,#764ba2); color:#fff; border-color:#667eea; }
      `}</style>

      {/* Page header */}
      <div className="flex items-center justify-between mb-5 pb-4 border-b-2 border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 m-0">Consignment Tracking</h2>
          <p className="text-sm text-gray-500 mt-1 m-0">Real-time Shipment Status &amp; Location</p>
        </div>
        <button onClick={() => setShowHelp(true)} title="How this page works"
          className="no-print flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all duration-150">
          <HelpCircle size={18}/>
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-5 no-print">
        <form onSubmit={handleSearch} className="max-w-[700px] mx-auto">
          <div className="flex items-center gap-2.5 border-2 border-gray-200 rounded-xl px-2.5 py-1.5 bg-gray-50 transition-all duration-200 focus-within:border-violet-400 focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(102,126,234,0.1)]">
            <input type="text" placeholder="Enter Consignment Number (e.g., LT-000123)"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 border-0 outline-none py-2.5 px-2 text-[15px] font-medium text-gray-900 bg-transparent" />
            <button type="submit" disabled={loading}
              className="flex-shrink-0 px-7 py-2.5 border-0 rounded-lg font-bold text-sm text-white cursor-pointer tracking-wide disabled:opacity-60 disabled:cursor-not-allowed hover:-translate-y-px hover:shadow-[0_6px_16px_rgba(102,126,234,0.4)] transition-all duration-200"
              style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DARK})`, boxShadow:'0 4px 12px rgba(102,126,234,0.3)' }}>
              {loading ? 'Tracking…' : 'Track'}
            </button>
          </div>
        </form>
        {recentSearches.length > 0 && (
          <div className="flex items-center gap-2.5 mt-3 flex-wrap">
            <span className="text-[13px] font-semibold text-gray-500">Recent:</span>
            {recentSearches.map((s, i) => (
              <button key={i} onClick={() => handleQuickSearch(s)}
                className="chip bg-gray-100 text-gray-700 px-3 py-1 border border-gray-200 rounded-full text-xs font-semibold cursor-pointer transition-all duration-150">
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-300 text-red-700 rounded-lg mb-4 text-sm">
          <AlertCircle size={16} className="flex-shrink-0" />{error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-violet-600">
          <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-500 rounded-full animate-spin" />
          <p className="m-0 text-sm">Tracking your consignment…</p>
        </div>
      )}

      {/* Results */}
      {trackingData && !loading && (<>

        {/* Info bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-violet-50 border border-violet-200 border-b-0 rounded-t-xl text-sm font-semibold text-gray-800 flex-wrap gap-2.5">
          <div>
            <span className="font-extrabold text-violet-900 font-mono text-[15px]">Consignment No: {c?.ConsignmentNo}</span>
            {dueDate && <>
              <span className="text-gray-500 ml-2"> DueDate :</span>
              <span className="text-red-500 font-bold ml-1"> {dueDate}</span>
            </>}
          </div>
          <div className="flex gap-2.5 flex-wrap no-print">
            <button onClick={() => window.print()}
              className="flex items-center gap-2 px-5 py-2 border-0 rounded-full text-xs font-bold text-white cursor-pointer transition-all duration-200 hover:-translate-y-px"
              style={{ background:'#6b7280', boxShadow:'0 2px 8px rgba(0,0,0,0.15)' }}>
              <Printer size={15} /> Print
            </button>
            {c?.statusPOD == 1 && (
              <button className="flex items-center gap-2 px-5 py-2 border-0 rounded-full text-xs font-bold text-white cursor-pointer transition-all duration-200 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(102,126,234,0.4)]"
                style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DARK})`, boxShadow:'0 2px 8px rgba(102,126,234,0.3)' }}>
                <FileText size={15} /> Show POD
              </button>
            )}
          </div>
        </div>

        {/* Tracking events table */}
        <div className="border border-violet-200 border-t-0 rounded-b-xl overflow-hidden mb-5 bg-white shadow-sm">
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DARK})` }}>
                {['DATE','Transaction Number','Location','','Event'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-bold text-xs tracking-widest uppercase text-white whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trackingRows.length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-gray-400">No tracking events found</td></tr>
              ) : trackingRows.map(row => (
                <tr key={row.id} className="tr-hover border-b border-gray-50 last:border-b-0">
                  <td className="px-4 py-3 whitespace-nowrap text-[13px] text-gray-500 min-w-[140px]">{row.dateTime}</td>
                  <td className="px-4 py-3 font-mono text-[13px] text-violet-800 font-bold min-w-[150px]">{row.txn}</td>
                  <td className="px-4 py-3 font-semibold min-w-[200px]" style={{ color: VIOLET }}>{row.location}</td>
                  <td className="px-4 py-3 w-14 text-center text-xl">🚚</td>
                  <td className="px-4 py-3 text-[13px] text-gray-800"><EventCell text={row.event} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Status History */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
          <div className="flex items-center gap-2 text-[0.95rem] font-bold text-gray-800 mb-4 pb-3 border-b-2 border-gray-100">
            <Clock size={17} className="text-blue-500" />
            Status History ({statusHistory.length} {statusHistory.length === 1 ? 'update' : 'updates'})
          </div>
          {statusHistory.length === 0 ? (
            <div className="py-7 text-center bg-gray-50 rounded-lg text-gray-400">
              <Clock size={36} className="mx-auto mb-2 opacity-20" />
              <div className="font-semibold text-[0.9rem]">No status updates yet</div>
            </div>
          ) : (
            <table className="w-full border-collapse text-[0.84rem]">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  {['#','Date & Time','Status','Event'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left font-bold text-[0.73rem] uppercase tracking-wide text-gray-600 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {statusHistory.map((s, i) => (
                  <tr key={i} className="sh-hover border-b border-gray-50 last:border-b-0">
                    <td className="px-3 py-2.5 text-center font-semibold text-gray-400 w-10">{i + 1}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {new Date(s.StatusDate).toLocaleString('en-IN', { day:'2-digit', month:'numeric', year:'numeric', hour:'2-digit', minute:'2-digit', hour12: false })}
                      </span>
                    </td>
                    <td className="px-3 py-2.5"><CourierBadge name={s.StatusName || s.Status} /></td>
                    <td className="px-3 py-2.5 text-gray-500">{s.Remarks || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Delivery confirmation */}
        {c?.DeliveryDate && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-5 py-4 mb-4 text-sm">
            <div className="flex items-center gap-2 font-bold text-emerald-800 mb-2.5">
              <CheckCircle size={16} /> Delivery Confirmed
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))' }}>
              <div>
                <div className="text-[11px] font-bold text-gray-500 uppercase mb-0.5">Delivered On</div>
                <div className="font-bold text-emerald-800">{new Date(c.DeliveryDate).toLocaleString('en-IN')}</div>
              </div>
              {c.ReceivedBy && (
                <div>
                  <div className="text-[11px] font-bold text-gray-500 uppercase mb-0.5">Received By</div>
                  <div className="font-bold text-emerald-800">{c.ReceivedBy}</div>
                </div>
              )}
              {c.DeliveredBy && (
                <div>
                  <div className="text-[11px] font-bold text-gray-500 uppercase mb-0.5">Delivered By</div>
                  <div className="font-bold text-emerald-800">{c.DeliveredBy}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Remarks */}
        {c?.Remarks && (
          <div className="bg-white border border-gray-200 rounded-lg px-5 py-4 text-sm text-gray-600">
            <div className="font-bold mb-1.5 text-gray-900">Remarks</div>
            {c.Remarks}
          </div>
        )}

      </>)}

      {/* Empty state */}
      {!trackingData && !loading && !error && (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200 text-gray-500">
          <Package size={72} className="mx-auto mb-4 text-gray-200" />
          <h3 className="text-[22px] font-bold text-gray-800 mb-2 mt-0">Track Your Consignment</h3>
          <p className="text-[15px] mb-7 m-0">Enter your consignment number above to see real-time status</p>
          <div className="flex justify-center gap-8 flex-wrap">
            {[[CheckCircle,'Real-time Updates'],[MapPin,'Location Tracking'],[Clock,'Delivery Timeline']].map(([Icon,label]) => (
              <div key={label} className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: VIOLET }}>
                <Icon size={18} /><span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showHelp && (
        <div className="fixed inset-0 flex items-center justify-center p-5 no-print" style={{ background: 'rgba(0,0,0,0.65)', zIndex: 999999 }}
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-xl w-full max-w-[600px] max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 rounded-t-xl flex-shrink-0"
              style={{ background: `linear-gradient(135deg,${VIOLET},${VIOLET_DARK})` }}>
              <h3 className="flex items-center gap-2 text-lg font-semibold m-0 text-white">
                <HelpCircle size={18} /> How to Use Consignment Tracking
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg border-0 cursor-pointer bg-white/20 hover:bg-white/30 transition-colors duration-150 text-white">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {[
                { title: 'Search', desc: 'Enter a consignment number (e.g., LT-000123) and click "Track", or pick one from your Recent searches below the search box.' },
                { title: 'Tracking Events', desc: 'The main table shows a chronological history of scan/movement events — date, location, and what happened at each step.' },
                { title: 'Status History', desc: 'Shows every courier status update recorded for this consignment, with date, status, and any remarks.' },
                { title: 'Delivery Confirmation', desc: 'Once delivered, a green confirmation panel shows the delivery date/time and who delivered and received it.' },
                { title: 'Print / POD', desc: 'Use "Print" to print the tracking page. If a POD (Proof of Delivery) is on file, a "Show POD" button appears.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: `linear-gradient(135deg,${VIOLET},${VIOLET_DARK})` }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
                style={{ background: `linear-gradient(135deg,${VIOLET},${VIOLET_DARK})` }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsignmentTracking;